import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog_VM, DialogFrame } from "./dialog-base";
import { StrucRow } from "../StructureElements/StrucRow";
import { StrucCol } from "../StructureElements/StrucCol";
import { InputWrapper } from "../Input/InputWrapper";
import { InputLabel } from "../Input/InputLabel";
import { CustomTextArea } from "../Input/CustomInput";
import {
  ApprovalGroup,
  ApprovalStatus,
} from "../../../core/models/entities/Approval";
import { Role } from "../../../core/models/entities/Role";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import IconButton from "../LogoButton/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPenToSquare, faX } from "@fortawesome/free-solid-svg-icons";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { canEditCommittedNavLogo } from "../../../utils/functions/logoRejectAndEdit";

interface Props {
  approval?: ApprovalGroup;
  title: JSX.Element;
  dialogOptions: Dialog_VM;
  exclude: Role;
  onEdit?: () => void;
  onCancel?: () => void;
  isOrder?: boolean; // New prop to indicate if this is for an order
  logo?: BaseLogo; // Logo data to check for product SKU
  // false hides Edit whatever the logo gate below says (the design list under
  // flag designRejectionNoteEditEnabled decides with the popup's own gate);
  // undefined/true = the logo gate alone, as today.
  showEdit?: boolean;
}

export function RejectionNoteDialog({
  approval,
  title,
  dialogOptions,
  exclude,
  onEdit,
  onCancel,
  isOrder = false, // Default to false for backward compatibility
  logo, // Logo data to check for product SKU
  showEdit,
}: Props) {
  const { t } = useTranslation();
  return (
    <DialogFrame dialogOption={dialogOptions}>
      <div className={"space-y-4"}>
        <StrucRow>
          <StrucCol>{title}</StrucCol>
        </StrucRow>
        {approval?.vendor?.status === ApprovalStatus.Rejected && (
          <StrucRow>
            <StrucCol>
              <InputWrapper>
                <InputLabel>{t("compDialogsInput.vendorNote")}</InputLabel>
                <CustomTextArea
                  readOnly={true}
                  value={
                    approval.vendor?.rejectionNote ??
                    t("compDialogsInput.noReasonGiven")
                  }
                />
              </InputWrapper>
            </StrucCol>
          </StrucRow>
        )}
        {approval?.customer?.status === ApprovalStatus.Rejected && (
          <StrucRow>
            <StrucCol>
              <InputWrapper>
                <InputLabel>{t("compDialogsInput.customerNote")}</InputLabel>
                <CustomTextArea
                  readOnly={true}
                  value={
                    approval.customer?.rejectionNote ??
                    t("compDialogsInput.noReasonGiven")
                  }
                />
              </InputWrapper>
            </StrucCol>
          </StrucRow>
        )}
        {approval?.external?.status === ApprovalStatus.Rejected && (
          <StrucRow>
            <StrucCol>
              <InputWrapper>
                <InputLabel>
                  {t("compDialogsInput.externalCustomerNote")}
                </InputLabel>
                <CustomTextArea
                  readOnly={true}
                  value={
                    approval.external?.rejectionNote ??
                    t("compDialogsInput.noReasonGiven")
                  }
                />
              </InputWrapper>
            </StrucCol>
          </StrucRow>
        )}
      </div>
      <div className="flex flex-row justify-between gap-3">
        <IconButton
          variant="dim"
          icon={<FontAwesomeIcon icon={faX} />}
          onClick={onCancel || dialogOptions.closeDialog}
          className="px-4"
        >
          {t("cancel")}
        </IconButton>
        {/* Edit: a logo not yet committed to the supplier (no SKU) - and,
            under flag logoRejectAndEditNavEnabled, a committed NAV logo the
            dealer rejected themselves that the API calls eligible while no
            write-back is pending (the SAME item number; the same gate as the
            popup's Rejected layout). Flag off = false = today's condition. */}
        {showEdit !== false &&
          !isOrder &&
          ((!logo?.productSku && !logo?.product?.sku) ||
            (!!logo && canEditCommittedNavLogo(logo))) && (
          <IconButton
            variant="success"
            icon={<FontAwesomeIcon icon={faPenToSquare} />}
            onClick={onEdit}
            key={"edit-logo"}
            className="px-4"
          >
            {t("edit")}
          </IconButton>
        )}
      </div>
    </DialogFrame>
  );
}
